import {useEffect, useRef, useState} from "react";
import {useDrag, useDrop} from "react-dnd";
import LocalizationInput from "../../elements/localizationInput.element";
import LocalizationTextArea from "../../elements/localizationTextArea.element";
import NumberInputLabel from "../../elements/numberInputLabel.element";
import {Metadata, UnitData} from "../../models/remoteConstants.model";
import MetadataLabel from "../../elements/metadataLabel.element";
import BooleanInput from "../../elements/booleanInput.element";
import {UpdateUnitCommand} from "../../commands/updateUnit.command";
import {useIntegrator} from "../../integrator.context";
import IconButton from "../../elements/IconButton";
import ImagePackElement from "../../elements/imagePack.element";
import List from "../../elements/list";
import ResizeBar, {useResizable} from "../../elements/resizeBar";
import Collapsible from "../../elements/collapsible";
import {useApp} from "../../app.context";
import PromptDialog from "../../elements/promptDialog.element";
import {getMetadataDisplayInfo, getDuplicateIds} from "../../utils/metadata.utils";
import {buildUnitDependencyLookup, getMissingDependencyTooltip} from "../../utils/dependencies";
import {MetadataCollection} from "../../commands/metadataValue.command";
import PaletteTabButton, {usePaletteTab} from "../../elements/paletteTabButton";


interface EntityEditorProps {
    name: string;
    entities: EntityProps[];
    metadata: Metadata[];
    // Fresh-ref object that signals "navigate to and open this entity". When
    // its reference changes and matches an EntityItem's id, that item opens
    // and scrolls into view. Undefined while no navigation has occurred.
    focusedEntity?: { id: string };
    addNewEntity(name: string): void;
    addNewAttribute(attribute: string): void;
    deleteEntity(id: string): void;
    deleteAttribute(attribute: string): void;
    addEntityAttribute(id: string, attribute: string): void;
    deleteEntityAttribute(id: string, attribute: string): void;
}

export interface EntityProps {
    id: string;
    unit: UnitData;
    statistics: StatisticProps[];
    attributes: string[];
}

interface StatisticProps {
    // Optional: when omitted (field-bound stats), the label comes from the model's
    // [FieldDoc] via the manifest. Armour and other unconstrained stats still set it.
    name?: string;
    value: number;
    tooltip?: string;
    // Manifest field key (e.g. "unit.properties.attack") — supplies the label,
    // tooltip, and range from the Client model, and clamps this input.
    field?: string;
    onChange(value: number): void;
}

function EntityEditor(props: EntityEditorProps) {
    const {getLocalizationValue, isHiddenUnitAttribute} = useApp();
    const [showPrompt, setShowPrompt] = useState(false);
    const dupes = getDuplicateIds(props.metadata);

    // Split palette into capability attributes (top) and type markers
    // (bottom). Type markers are auto-seeded from the manifest and never
    // carry parameters, so they belong in their own group.
    const visibleMetadata = props.metadata.filter(m => !isHiddenUnitAttribute(m.id));
    const typeMetadata = props.metadata.filter(m => isHiddenUnitAttribute(m.id));

    // ---- Resizable palette (the right-hand Attributes/Types column) ------
    // A vertical bar drags the palette width; tabs at the top switch between
    // the Attributes and Types lists. Width persists to localStorage. See
    // elements/resizeBar; no collapse buttons here.
    const containerRef = useRef<HTMLDivElement>(null);
    const palette = useResizable({
        storageKey: 'rift.editor.unitsPaletteWidth',
        axis: 'x', min: 240, invert: true,                      // palette sits right of the bar
        initial: () => Math.round(window.innerWidth * 0.25),    // old fixed 25vw
        // Leave the units list at least this wide.
        max: () => (containerRef.current?.getBoundingClientRect().width ?? Infinity) - 320,
    });
    const [paletteTab, setPaletteTab] = usePaletteTab('rift.editor.unitsPaletteTab', 'Attributes');

    // The local MetadataDragLabel doesn't actually render a delete button —
    // the palette is drag-only. Kept the `onDelete` prop on its signature for
    // a future surface change; passing the deleter for capability attributes
    // and omitting it for type markers leaves the door open without breaking
    // current rendering.
    const renderDragLabel = (element: typeof props.metadata[number], allowDelete: boolean) => (
        <MetadataDragLabel uid={element.uid} id={element.id} values={element.values} duplicate={dupes.has(element.id)} onDelete={allowDelete ? () => {
            props.deleteAttribute(element.uid);
        } : undefined}/>
    );

    return (
        <div ref={containerRef} className={'layout horizontal'} style={{flexGrow: 1}}>
            <div className={'layout vertical gap'} style={{flexGrow: 1, gap: 10, minWidth: 0}}>
                <List title={'Units'} elements={props.entities}
                      headerClassName={'box'}
                      headerAction={<button style={{whiteSpace: 'nowrap'}} onClick={() => setShowPrompt(true)}>{`Create new ${props.name}`}</button>}
                      component={element => <EntityItem entity={element} parent={props} dupes={dupes}/>}
                      search={element => getLocalizationValue(element.id)}
                      keyFor={element => element.id}
                />
                {showPrompt && <PromptDialog
                    title={`New ${props.name}`}
                    placeholder={`Enter ${props.name.toLowerCase()} name...`}
                    existingNames={props.entities.map(e => getLocalizationValue(e.id))}
                    onConfirm={(name) => { props.addNewEntity(name); setShowPrompt(false); }}
                    onCancel={() => setShowPrompt(false)}
                />}
            </div>

            {/* Draggable bar to resize the palette width (drags horizontally). */}
            <ResizeBar axis={'x'} {...palette.handleProps}/>

            <div className={'layout vertical'} style={{width: palette.size, flexShrink: 0}}>
                <div className={'layout vertical gap box'} style={{height: 0, flexGrow: 1, overflowY: 'auto'}}>
                    <div className={'layout horizontal gap'} style={{width: '100%'}}>
                        <PaletteTabButton label={'Attributes'} current={paletteTab} onClick={setPaletteTab}/>
                        <PaletteTabButton label={'Types'} current={paletteTab} onClick={setPaletteTab}/>
                    </div>
                    <div className={'layout vertical gap'} style={{flexGrow: 1, overflowY: 'auto'}}>
                        {paletteTab === 'Attributes' && [...visibleMetadata]
                            .sort((a, b) => getLocalizationValue(a.id).localeCompare(getLocalizationValue(b.id)))
                            .map(element => <div key={element.uid}>{renderDragLabel(element, true)}</div>)}
                        {paletteTab === 'Types' && [...typeMetadata]
                            .sort((a, b) => getLocalizationValue(a.id).localeCompare(getLocalizationValue(b.id)))
                            .map(element => <div key={element.uid}>{renderDragLabel(element, false)}</div>)}
                    </div>
                </div>
            </div>
        </div>
    )
}

function EntityItem(props: { entity: EntityProps, parent: EntityEditorProps, dupes: Set<string> }) {
    const {executeCommand, state} = useIntegrator();
    const {getLocalizationValue, confirmDelete, getAttributeManifestEntry} = useApp();
    const deleteDisabled = props.parent.entities.length <= 1;

    // Partition assigned attributes by their manifest's Hidden flag. Hidden
    // ones (e.g. Ranged, Melee, Siege) are type *markers* — game code still
    // queries them via HasAttribute, but they're presented under their own
    // header so the visible list isn't muddied with category labels.
    function isHiddenAttribute(uid: string): boolean {
        const meta = props.parent.metadata.find(m => m.uid === uid);
        if (!meta) return false;
        return getAttributeManifestEntry(meta.id, MetadataCollection.Units)?.Hidden === true;
    }
    const visibleAttributes = props.entity.attributes.filter(a => !isHiddenAttribute(a));
    const typeAttributes = props.entity.attributes.filter(a => isHiddenAttribute(a));

    // Dependency markers are only wired for unit attributes (the shared
    // EntityEditor renders both Units and — historically — Buildings).
    const missingDepsFor = props.parent.name === 'Unit'
        ? buildUnitDependencyLookup(props.entity.attributes, state)
        : () => [];

    const [open, setOpen] = useState(false);
    const rowRef = useRef<HTMLDivElement | null>(null);

    // Re-fires on every navigation event because focusedEntity is a fresh
    // object reference each call, even when the same id is targeted twice.
    useEffect(() => {
        if (props.parent.focusedEntity?.id === props.entity.id) {
            setOpen(true);
            rowRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
    }, [props.parent.focusedEntity, props.entity.id]);

    const [{canDrop}, drop] = useDrop({
        accept: 'attribute',
        drop: (item: any) => props.parent.addEntityAttribute(props.entity.id, item.attribute),
        collect: (monitor) => {
            return {
                canDrop: monitor.isOver({shallow: true}) &&
                    props.entity.attributes.find(a => a === monitor.getItem().attribute) === undefined
            };
        },
    });

    const header = (
        <div className={'layout horizontal'} style={{gap: 10, alignItems: 'center'}}>
            <ImagePackElement id={props.entity.id} level={0} size={80}/>
            <LocalizationInput style={{width: 100}} id={props.entity.id}/>
            <BooleanInput value={props.entity.unit.active} onChange={(value) => {
                props.entity.unit.active = value;
                executeCommand(new UpdateUnitCommand(props.entity.unit));
            }}/>
            <IconButton icon={'trash'} style={{opacity: deleteDisabled ? 0.5 : 1}} onClick={async (e) => {
                e.stopPropagation();
                if (!deleteDisabled && await confirmDelete(getLocalizationValue(props.entity.id), props.parent.name.toLowerCase())) {
                    props.parent.deleteEntity(props.entity.id);
                }
            }}/>
        </div>
    );

    return (
        <div ref={(el) => { drop(el); rowRef.current = el; }} className={'box transition'} style={{
            opacity: props.entity.unit.active ? 1 : 0.5,
            border: `solid 1px ${canDrop ? 'var(--tint-color)' : 'var(--overlay-color)'}`
        }}>
            <Collapsible title={header} defaultOpen={false} open={open} onOpenChange={setOpen}>
                <div className={'layout vertical gap'} style={{gap: 6, marginTop: 10}}>
                    <p className={'text header'}>Description</p>
                    <LocalizationTextArea id={`${props.entity.id}.description`} style={{minHeight: 50}}/>
                </div>
                <div className={'layout horizontal gap'} style={{gap: 20, marginTop: 10}}>
                    <div className={'layout vertical flex-grow'} style={{gap: 10}}>
                        <div className={'layout vertical gap flex-grow'} style={{borderRadius: '5px'}}>
                            <List title={'Attributes'} elements={visibleAttributes}
                                  component={element => renderAssignedAttribute(element, props, missingDepsFor, getLocalizationValue)}
                                  search={element => getLocalizationValue(element)}
                                  keyFor={element => element}
                            />
                            {typeAttributes.length > 0 && (
                                <List title={'Types'} elements={typeAttributes}
                                      component={element => renderAssignedAttribute(element, props, missingDepsFor, getLocalizationValue)}
                                      search={element => getLocalizationValue(element)}
                                      keyFor={element => element}
                                />
                            )}
                        </div>
                    </div>
                    <div className={'layout vertical gap flex-grow'}>
                        <p className={'text header'}>Statistics</p>
                        {props.entity.statistics.map((statistic, i) => {
                            return <NumberInputLabel key={i} title={statistic.name} value={statistic.value}
                                                     tooltip={statistic.tooltip}
                                                     field={statistic.field}
                                                     onChange={statistic.onChange}/>
                        })}
                    </div>
                </div>
            </Collapsible>
        </div>
    )
}

function renderAssignedAttribute(
    element: string,
    props: { entity: EntityProps, parent: EntityEditorProps, dupes: Set<string> },
    missingDepsFor: (uid: string) => string[],
    getLocalizationValue: (key: string) => string,
) {
    const meta = props.parent.metadata.find(m => m.uid === element);
    const dup = props.dupes.has(meta?.id ?? '');
    const {suffix, tooltip} = getMetadataDisplayInfo(meta, dup, getLocalizationValue);
    const missing = missingDepsFor(element);
    const warning = missing.length > 0 ? getMissingDependencyTooltip(missing) : undefined;
    return (
        <div title={tooltip}>
            <MetadataLabel uid={element} suffix={suffix} warning={warning} onDelete={() => props.parent.deleteEntityAttribute(props.entity.id, element)}/>
        </div>
    );
}

function MetadataDragLabel(props: { uid: string, id: string, values?: { [key: string]: number }, duplicate?: boolean, onDelete?(): void }) {
    const [{opacity}, drag] = useDrag({
        type: 'attribute',
        item: {
            attribute: props.uid,
        },
        collect: (monitor) => {
            const isDragging = monitor.isDragging();
            const opacity = isDragging ? 0.5 : 1;
            return {
                opacity: opacity,
            };
        },
    });

    const style = {
        opacity: opacity,
    };

    const {getLocalizationValue} = useApp();
    const meta = props.id ? {id: props.id, values: props.values ?? {}} as any : undefined;
    const {suffix, tooltip} = getMetadataDisplayInfo(meta, props.duplicate ?? false, getLocalizationValue);

    return (
        <div ref={drag} style={style} title={tooltip}>
            <MetadataLabel uid={props.uid} suffix={suffix}/>
        </div>
    );
}

export default EntityEditor;